import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, BookOpen, Mail, Crown, ChevronRight, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import useSeo from "../lib/useSeo";

export default function AdminPage() {
    useSeo({
        title: "Sovereign Console",
        description: "EIGOO Books Hub admin console — manage comics, chapters, newsletter subscribers and pre-order leads.",
    });
    const [tab, setTab] = useState("comics");
    const [comics, setComics] = useState([]);
    const [leads, setLeads] = useState({ newsletter: [], preorders: [] });
    const [showNewComic, setShowNewComic] = useState(false);
    const [showNewChapter, setShowNewChapter] = useState(null); // comic id
    const [error, setError] = useState("");

    const load = async () => {
        const [comicsRes, leadsRes] = await Promise.all([
            api.get("/comics"),
            api.get("/admin/leads"),
        ]);
        setComics(comicsRes.data);
        setLeads(leadsRes.data);
    };

    useEffect(() => {
        load();
    }, []);

    const deleteComic = async (id) => {
        if (!window.confirm("Delete this comic and all its chapters?")) return;
        await api.delete(`/comics/${id}`);
        await load();
    };

    return (
        <main data-testid="admin-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
            <div className="mb-12 flex items-end justify-between">
                <div>
                    <div className="eyebrow mb-3">Sovereign Console</div>
                    <h1 className="font-serif text-5xl md:text-6xl">Admin</h1>
                </div>
                <Crown className="w-7 h-7 text-[#D4AF37]" />
            </div>

            <div className="flex gap-2 mb-10 border-b" style={{ borderColor: "rgba(212,175,55,0.18)" }}>
                {[
                    { k: "comics", label: "Comics & Chapters" },
                    { k: "leads", label: "Leads & Subscribers" },
                ].map((t) => (
                    <button
                        key={t.k}
                        data-testid={`admin-tab-${t.k}`}
                        onClick={() => setTab(t.k)}
                        className={`px-5 py-3 text-xs uppercase tracking-widest transition-colors border-b-2 -mb-px ${
                            tab === t.k
                                ? "text-[#D4AF37] border-[#D4AF37]"
                                : "text-[#A1A1AA] border-transparent hover:text-[#E6D59A]"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {error && (
                <div className="mb-6 border border-[#8A0303]/50 bg-[#8A0303]/10 px-4 py-3 text-sm text-[#cd6a6a]">
                    {error}
                </div>
            )}

            {tab === "comics" && (
                <div>
                    <div className="flex justify-between mb-6">
                        <div className="text-[#A1A1AA] text-sm">
                            {comics.length} title{comics.length === 1 ? "" : "s"}
                        </div>
                        <button
                            data-testid="add-comic-btn"
                            onClick={() => setShowNewComic(true)}
                            className="btn-ghost-gold !py-2 !px-4 text-[10px]"
                        >
                            <Plus className="w-3.5 h-3.5" /> New title
                        </button>
                    </div>

                    <div className="space-y-4">
                        {comics.map((c) => (
                            <ComicAdminRow
                                key={c.id}
                                comic={c}
                                onDelete={() => deleteComic(c.id)}
                                onAddChapter={() => setShowNewChapter(c.id)}
                                onChange={load}
                                onError={setError}
                            />
                        ))}
                    </div>
                </div>
            )}

            {tab === "leads" && (
                <div className="grid md:grid-cols-2 gap-8">
                    <div className="frame-gold p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <Mail className="w-5 h-5 text-[#D4AF37]" />
                            <h3 className="font-serif text-2xl">Newsletter</h3>
                            <span className="ml-auto eyebrow text-[10px]">
                                {leads.newsletter.length} subs
                            </span>
                        </div>
                        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                            {leads.newsletter.length === 0 && (
                                <p className="text-[#A1A1AA] italic text-sm">No subscribers yet.</p>
                            )}
                            {leads.newsletter.map((n, i) => (
                                <div
                                    key={i}
                                    data-testid={`newsletter-${i}`}
                                    className="flex items-center justify-between py-2 border-b text-sm"
                                    style={{ borderColor: "rgba(212,175,55,0.1)" }}
                                >
                                    <span className="text-white">{n.email}</span>
                                    <span className="text-[#5a5a5a] text-xs">
                                        {n.subscribed_at?.slice(0, 10)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="frame-gold p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <BookOpen className="w-5 h-5 text-[#D4AF37]" />
                            <h3 className="font-serif text-2xl">Pre-orders</h3>
                            <span className="ml-auto eyebrow text-[10px]">
                                {leads.preorders.length} reservations
                            </span>
                        </div>
                        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                            {leads.preorders.length === 0 && (
                                <p className="text-[#A1A1AA] italic text-sm">No pre-orders yet.</p>
                            )}
                            {leads.preorders.map((p, i) => (
                                <div
                                    key={i}
                                    data-testid={`preorder-${i}`}
                                    className="py-2 border-b text-sm"
                                    style={{ borderColor: "rgba(212,175,55,0.1)" }}
                                >
                                    <div className="flex justify-between">
                                        <span className="text-white">{p.name}</span>
                                        <span className="text-[#D4AF37] text-xs uppercase tracking-widest">
                                            {p.edition} × {p.quantity}
                                        </span>
                                    </div>
                                    <div className="text-[#A1A1AA] text-xs">{p.email}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {showNewComic && (
                <NewComicModal
                    onClose={() => setShowNewComic(false)}
                    onCreated={async () => {
                        setShowNewComic(false);
                        await load();
                    }}
                    onError={setError}
                />
            )}
            {showNewChapter && (
                <NewChapterModal
                    comicId={showNewChapter}
                    onClose={() => setShowNewChapter(null)}
                    onCreated={async () => {
                        setShowNewChapter(null);
                        await load();
                    }}
                    onError={setError}
                />
            )}
        </main>
    );
}

function ComicAdminRow({ comic, onDelete, onAddChapter }) {
    const [open, setOpen] = useState(false);
    const [chapters, setChapters] = useState([]);

    const loadChapters = async () => {
        const { data } = await api.get(`/comics/${comic.slug}`);
        setChapters(data.chapters || []);
    };

    useEffect(() => {
        if (open) loadChapters();
    }, [open]);

    const deleteChapter = async (id) => {
        if (!window.confirm("Delete chapter?")) return;
        await api.delete(`/chapters/${id}`);
        await loadChapters();
    };

    return (
        <div
            data-testid={`admin-comic-${comic.slug}`}
            className="frame-gold p-5"
        >
            <div className="flex items-center gap-5">
                <div
                    className="w-14 h-20 bg-cover bg-center flex-shrink-0"
                    style={{ backgroundImage: `url(${comic.cover_url})` }}
                />
                <div className="flex-1">
                    <div className="font-serif text-xl text-white">{comic.title}</div>
                    <div className="text-xs text-[#A1A1AA] italic">{comic.tagline}</div>
                    <div className="eyebrow text-[10px] mt-1">
                        {comic.chapter_count} chapter{comic.chapter_count === 1 ? "" : "s"} ·{" "}
                        {comic.is_flagship ? "Flagship" : comic.accent}
                    </div>
                </div>
                <button
                    onClick={() => setOpen(!open)}
                    className="text-[#A1A1AA] hover:text-[#D4AF37] text-xs uppercase tracking-widest"
                    data-testid={`expand-${comic.slug}`}
                >
                    {open ? "Hide" : "Manage"} <ChevronRight className={`w-3 h-3 inline transition-transform ${open ? "rotate-90" : ""}`} />
                </button>
                <button
                    data-testid={`delete-comic-${comic.slug}`}
                    onClick={onDelete}
                    className="text-[#cd6a6a]/70 hover:text-[#cd6a6a]"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>

            {open && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-5 pt-5 border-t"
                    style={{ borderColor: "rgba(212,175,55,0.15)" }}
                >
                    <div className="flex justify-between items-center mb-4">
                        <div className="eyebrow">Chapters</div>
                        <button
                            data-testid={`add-chapter-${comic.slug}`}
                            onClick={onAddChapter}
                            className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-[#E6D59A] flex items-center gap-1"
                        >
                            <Plus className="w-3 h-3" /> Add chapter
                        </button>
                    </div>
                    <div className="space-y-2">
                        {chapters.map((ch) => (
                            <div
                                key={ch.id}
                                className="flex items-center justify-between py-2 px-3 bg-[#0a0a0a] border"
                                style={{ borderColor: "rgba(212,175,55,0.1)" }}
                            >
                                <div className="text-sm text-white">
                                    <span className="text-[#D4AF37] mr-3">
                                        {String(ch.order).padStart(2, "0")}
                                    </span>
                                    {ch.title}
                                </div>
                                <button
                                    onClick={() => deleteChapter(ch.id)}
                                    className="text-[#cd6a6a]/70 hover:text-[#cd6a6a]"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                        {chapters.length === 0 && (
                            <p className="text-[#A1A1AA] italic text-sm">No chapters yet.</p>
                        )}
                    </div>
                </motion.div>
            )}
        </div>
    );
}

function ModalShell({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm">
            <div className="frame-gold p-8 max-w-xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="font-serif text-2xl">{title}</h3>
                    <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#D4AF37]">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

function NewComicModal({ onClose, onCreated, onError }) {
    const [form, setForm] = useState({
        title: "",
        slug: "",
        tagline: "",
        synopsis: "",
        cover_url: "",
        accent: "gold",
        is_flagship: false,
    });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/comics", form);
            onCreated();
        } catch (err) {
            onError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const fields = [
        { k: "title", label: "Title" },
        { k: "slug", label: "Slug (url-safe)" },
        { k: "tagline", label: "Tagline" },
        { k: "cover_url", label: "Cover image URL" },
    ];

    return (
        <ModalShell title="New Title" onClose={onClose}>
            <form onSubmit={submit} className="space-y-4" data-testid="new-comic-form">
                {fields.map((f) => (
                    <div key={f.k}>
                        <label className="eyebrow text-[10px] block mb-1.5">{f.label}</label>
                        <input
                            data-testid={`new-comic-${f.k}`}
                            required
                            value={form[f.k]}
                            onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                            className="w-full bg-[#141414] border px-4 py-3 text-white"
                            style={{ borderColor: "rgba(212,175,55,0.2)" }}
                        />
                    </div>
                ))}
                <div>
                    <label className="eyebrow text-[10px] block mb-1.5">Synopsis</label>
                    <textarea
                        rows={3}
                        value={form.synopsis}
                        onChange={(e) => setForm({ ...form, synopsis: e.target.value })}
                        className="w-full bg-[#141414] border px-4 py-3 text-white"
                        style={{ borderColor: "rgba(212,175,55,0.2)" }}
                    />
                </div>
                <label className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <input
                        type="checkbox"
                        checked={form.is_flagship}
                        onChange={(e) => setForm({ ...form, is_flagship: e.target.checked })}
                    />
                    Mark as flagship
                </label>
                <button
                    type="submit"
                    disabled={loading}
                    className="btn-gold w-full justify-center"
                >
                    {loading ? "Creating…" : "Create Title"}
                </button>
            </form>
        </ModalShell>
    );
}

function NewChapterModal({ comicId, onClose, onCreated, onError }) {
    const [form, setForm] = useState({ title: "", order: 1 });
    const [pages, setPages] = useState([{ image_url: "", caption: "", sfx: "" }]);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post(`/comics/${comicId}/chapters`, { ...form, pages });
            onCreated();
        } catch (err) {
            onError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <ModalShell title="New Chapter" onClose={onClose}>
            <form onSubmit={submit} className="space-y-4" data-testid="new-chapter-form">
                <div>
                    <label className="eyebrow text-[10px] block mb-1.5">Title</label>
                    <input
                        required
                        data-testid="new-chapter-title"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className="w-full bg-[#141414] border px-4 py-3 text-white"
                        style={{ borderColor: "rgba(212,175,55,0.2)" }}
                    />
                </div>
                <div>
                    <label className="eyebrow text-[10px] block mb-1.5">Order</label>
                    <input
                        type="number"
                        min={1}
                        value={form.order}
                        onChange={(e) =>
                            setForm({ ...form, order: parseInt(e.target.value || "1", 10) })
                        }
                        className="w-full bg-[#141414] border px-4 py-3 text-white"
                        style={{ borderColor: "rgba(212,175,55,0.2)" }}
                    />
                </div>
                <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className="eyebrow text-[10px]">Pages</label>
                        <button
                            type="button"
                            onClick={() =>
                                setPages([...pages, { image_url: "", caption: "", sfx: "" }])
                            }
                            className="text-[10px] uppercase tracking-widest text-[#D4AF37] flex items-center gap-1"
                        >
                            <Plus className="w-3 h-3" /> Add
                        </button>
                    </div>
                    <div className="space-y-3">
                        {pages.map((p, i) => (
                            <div
                                key={i}
                                className="border p-3 space-y-2"
                                style={{ borderColor: "rgba(212,175,55,0.15)" }}
                            >
                                <input
                                    placeholder="Image URL"
                                    value={p.image_url}
                                    onChange={(e) => {
                                        const next = [...pages];
                                        next[i].image_url = e.target.value;
                                        setPages(next);
                                    }}
                                    className="w-full bg-[#141414] border px-3 py-2 text-sm text-white"
                                    style={{ borderColor: "rgba(212,175,55,0.2)" }}
                                />
                                <input
                                    placeholder="Caption"
                                    value={p.caption}
                                    onChange={(e) => {
                                        const next = [...pages];
                                        next[i].caption = e.target.value;
                                        setPages(next);
                                    }}
                                    className="w-full bg-[#141414] border px-3 py-2 text-sm text-white"
                                    style={{ borderColor: "rgba(212,175,55,0.2)" }}
                                />
                                <input
                                    placeholder="SFX (optional)"
                                    value={p.sfx}
                                    onChange={(e) => {
                                        const next = [...pages];
                                        next[i].sfx = e.target.value;
                                        setPages(next);
                                    }}
                                    className="w-full bg-[#141414] border px-3 py-2 text-sm text-white"
                                    style={{ borderColor: "rgba(212,175,55,0.2)" }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <button
                    type="submit"
                    disabled={loading}
                    className="btn-gold w-full justify-center"
                >
                    {loading ? "Creating…" : "Create Chapter"}
                </button>
            </form>
        </ModalShell>
    );
}
